/**
 * BudgetChartModal - Shows budget vs expense charts for account codes
 * with currency conversion using live exchange rates.
 */
import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend, CartesianGrid
} from 'recharts';
import { BarChart2, TrendingUp } from 'lucide-react';

export default function BudgetChartModal({ open, onClose, code, label, segment }) {
  const [chartTab, setChartTab] = useState('expense');
  const [stats, setStats] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [rate, setRate] = useState(1);
  const [baseCurrency, setBaseCurrency] = useState('USD');

  useEffect(() => {
    if (!code) return;
    const seg = segment || 4;
    api.get(`/api/account-codes/${seg}/${code}/stats`).then(r => setStats(r.data)).catch(() => {});
    api.get('/api/data/currencies', { params: { page_size: 100 } }).then(r => {
      const list = (r.data.data || []).map(c => c.code).filter(Boolean);
      if (!list.includes('USD')) list.unshift('USD');
      setCurrencies(list);
    }).catch(() => setCurrencies(['USD', 'GBP', 'EUR']));
    // Get base currency from system settings
    api.get('/api/auth/me').then(r => {
      const sc = r.data?.system_settings?.system_currency || 'USD';
      setBaseCurrency(sc);
      setSelectedCurrency(sc);
    }).catch(() => {});
  }, [code, segment]);

  useEffect(() => {
    if (selectedCurrency === baseCurrency) { setRate(1); return; }
    api.get('/api/data/currencies/convert', { params: { from: baseCurrency, to: selectedCurrency, amount: 1 } })
      .then(r => setRate(r.data?.rate || 1))
      .catch(() => {
        // Fallback: try open.er-api
        fetch(`https://open.er-api.com/v6/latest/${baseCurrency}`)
          .then(r => r.json())
          .then(d => setRate(d.rates?.[selectedCurrency] || 1))
          .catch(() => setRate(1));
      });
  }, [selectedCurrency, baseCurrency]);

  if (!stats) return null;

  const cv = (v) => Math.round((v || 0) * rate * 100) / 100;
  const fmtV = (v) => cv(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const budget = stats.budget || 0;
  const spent = stats.spent_this_fy || 0;
  const available = stats.available || 0;
  const totalPrev = stats.total_spent_alltime || 0;

  const expenseData = [{ name: label || code, budget: cv(budget), spent: cv(spent) }];

  const runRateData = (stats.monthly_data || []).map((m, i) => ({
    month: m.month,
    budgetedRunRate: cv(stats.budgeted_run_rate?.[i] || 0),
    monthlyBudget: cv(m.budget),
    actualSpent: cv(m.spent),
  }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Available Budget — {code}</DialogTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Currency:</span>
              <select value={selectedCurrency} onChange={e => setSelectedCurrency(e.target.value)}
                className="h-7 rounded border border-input bg-background px-2 text-xs font-mono">
                {currencies.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {selectedCurrency !== baseCurrency && <span className="text-[10px] text-muted-foreground">1 {baseCurrency} = {rate.toFixed(4)} {selectedCurrency}</span>}
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex gap-1 mb-4">
          <button onClick={() => setChartTab('expense')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${chartTab === 'expense' ? 'bg-primary text-white border-primary' : 'border-border hover:bg-accent'}`}>
            <BarChart2 className="h-3.5 w-3.5" /> Expense Chart
          </button>
          <button onClick={() => setChartTab('runrate')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${chartTab === 'runrate' ? 'bg-primary text-white border-primary' : 'border-border hover:bg-accent'}`}>
            <TrendingUp className="h-3.5 w-3.5" /> Run Rate Chart
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-6">
          <div>
            {chartTab === 'expense' ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={expenseData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => `${selectedCurrency} ${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="budget" name="Total Budget" fill="hsl(var(--chart-1))" radius={[3,3,0,0]} />
                  <Bar dataKey="spent" name="Total Spent" fill="hsl(var(--chart-4))" radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={runRateData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => `${selectedCurrency} ${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line type="monotone" dataKey="budgetedRunRate" name="Budgeted Run Rate" stroke="hsl(214,64%,33%)" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="monthlyBudget" name="Monthly Budget" stroke="hsl(142,70%,32%)" strokeDasharray="4 2" dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="actualSpent" name="Actual Spent" stroke="hsl(280,60%,50%)" dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          
          <div className="space-y-0">
            <div className="text-sm font-semibold mb-2">{code} ({label})</div>
            <table className="w-full text-xs border-collapse">
              <tbody>
                {chartTab === 'expense' ? (
                  <>
                    <tr className="border-b">
                      <td className="py-1.5 text-muted-foreground">This Financial Year Budget</td>
                      <td className="text-right font-mono">{selectedCurrency} {fmtV(budget)}</td>
                    </tr>
                    <tr className="border-b bg-primary/10">
                      <td className="py-1.5 font-medium" colSpan={2}>Current Financial Year Spent ({stats.fy_start} – {stats.fy_end})</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1.5 text-muted-foreground">Budget Spent So Far This FY</td>
                      <td className="text-right font-mono">{selectedCurrency} {fmtV(spent)}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1.5 text-muted-foreground">Available Budget</td>
                      <td className={`text-right font-mono font-bold ${available >= 0 ? 'text-green-600' : 'text-red-600'}`}>{selectedCurrency} {fmtV(available)}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1.5 text-muted-foreground">Budget Utilisation</td>
                      <td className="text-right font-mono">{stats.utilisation_pct?.toFixed(1)}%</td>
                    </tr>
                    <tr className="border-b bg-muted/50">
                      <td className="py-1.5 font-medium" colSpan={2}>Overall Spent To Date</td>
                    </tr>
                    <tr>
                      <td className="py-1.5 text-muted-foreground">Total Spent All Time</td>
                      <td className="text-right font-mono">{selectedCurrency} {fmtV(totalPrev)}</td>
                    </tr>
                  </>
                ) : (
                  <>
                    <tr className="border-b">
                      <td className="py-1.5 text-muted-foreground">Financial Year</td>
                      <td className="text-right">{stats.fy_start} – {stats.fy_end}</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1.5 text-muted-foreground">Code</td>
                      <td className="text-right font-mono">{code} ({label})</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-1.5 text-muted-foreground">Total Budget</td>
                      <td className="text-right font-mono">{selectedCurrency} {fmtV(budget)}</td>
                    </tr>
                    <tr>
                      <td className="py-1.5 text-muted-foreground">Budget Spent So Far</td>
                      <td className="text-right font-mono">{selectedCurrency} {fmtV(spent)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
